/// <amd-module name="Repos/Tfvc/Views/ChangeDetails/Components/ChangeHeader" />
import * as React from "react";
import { TfvcVersionType } from "Repos/Common/Contracts/Git";
import * as Resources from "Repos/Tfvc/Views/ChangeDetails/Resources";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { useComponentContext } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { ClipboardButton } from "VSSUI/Clipboard";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { useChangeDetailsContext } from "../ChangeDetailsContext";

export interface IChangeDetailsHeaderProps {
    screenSize: ScreenSize;
}

export const ChangeDetailsHeader = (props: IChangeDetailsHeaderProps): JSX.Element => {
    const { screenSize } = props;
    const { viewService } = useChangeDetailsContext();
    const { pageContext } = useComponentContext();

    const fullComment = viewService.getChangeFullComment();
    const shortComment = viewService.getChangeShortComment();
    const defaultText = viewService.getDefaultChangeText();
    const hasComment = fullComment !== defaultText;
    const changeOwner = viewService.getChangeOwner();
    const headerMenuItems = viewService.getHeaderMenuItems(screenSize <= ScreenSize.xsmall);

    const navService = pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
    navService.setPageTitle(viewService.getDocumentTitle());

    return (
        <CustomHeader>
            <div className="bolt-header-content-area flex-row flex-grow flex-self-stretch margin-bottom-4">
                <HeaderTitleArea className="shadow-padding">
                    <HeaderTitleRow className="repos-change-title-row text-ellipsis">
                        <HeaderTitle titleSize={TitleSize.Large} className="text-ellipsis flex-grow">
                            <FocusWithin>
                                {(focusStatus: IFocusWithinStatus) => (
                                    <div className="flex-row">
                                        <Tooltip overflowOnly={shortComment === fullComment} text={fullComment}>
                                            <span className="text-ellipsis" onBlur={focusStatus.onBlur} onFocus={focusStatus.onFocus} tabIndex={0}>
                                                {shortComment}
                                            </span>
                                        </Tooltip>
                                        <ClipboardButton
                                            ariaLabel={Resources.CopyTitle}
                                            className={css("margin-left-8 repos-change-title-clipboard-button", focusStatus.hasFocus && "visible")}
                                            getContent={() => fullComment}
                                            key="comment"
                                            onBlur={focusStatus.onBlur}
                                            onFocus={focusStatus.onFocus}
                                            showCopiedTooltip={true}
                                            subtle={true}
                                            tooltipProps={{
                                                text: Resources.CopyTitle
                                            }}
                                        />
                                    </div>
                                )}
                            </FocusWithin>
                        </HeaderTitle>
                        {headerMenuItems && headerMenuItems.length > 0 && <HeaderCommandBar items={headerMenuItems} />}
                    </HeaderTitleRow>
                    <div className="flex-row flex-center secondary-text rhythm-horizontal-8 flex-wrap">
                        {hasComment && <span>{defaultText}</span>}
                        <FormatComponent
                            className="flex-row flex-center rhythm-horizontal-4"
                            elementType="div"
                            format={
                                viewService.getVersionType() === TfvcVersionType.Shelveset ? Resources.ShelvedSubTitle : Resources.CheckedInSubTitle
                            }
                        >
                            <Persona personaProvider={viewService.getPersonaProvider()} identity={changeOwner} size={PersonaSize.size20} />
                            <Tooltip overflowOnly={true} text={changeOwner.displayName}>
                                <span className="font-weight-semibold margin-right-4 repos-change-owner text-ellipsis">
                                    {changeOwner.displayName}
                                </span>
                            </Tooltip>
                            <Ago date={viewService.getCheckinDate()} format={AgoFormat.Compact} />
                        </FormatComponent>
                    </div>
                </HeaderTitleArea>
            </div>
        </CustomHeader>
    );
};
